import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  useToast,
  FormErrorMessage,
  Flex,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  Spinner,
  Heading,
  Textarea,
  // Removed Image import as it's no longer explicitly used for duplicate preview
} from '@chakra-ui/react'
import Card from 'components/card/Card'
import { useGetAboutUs, useCreateOrUpdateAboutUs } from 'queries/about.query'
// import Dropzone from '../../settings/components/Dropzone'

// import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'


export default function AboutUsForm () {
  const inputColor = useColorModeValue('gray.800', 'white')
  const backgroundColor = useColorModeValue('white', 'gray.700')

  const toast = useToast()
  const { data: aboutData, isLoading, isError } = useGetAboutUs()
  const { mutate: createOrUpdateAbout, isPending } = useCreateOrUpdateAboutUs()

  const [formState, setFormState] = useState({
    sub_title: '',
    mission: '',
    vision: '',
    facebook_url: '',
    twitter_url: '',
    instagram_url: '',
    youtube_url: '',
  })

  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (aboutData) {
      setFormState({
        sub_title: aboutData.sub_title || '',
        mission: aboutData.mission || '',
        vision: aboutData.vision || '',
        facebook_url: aboutData.facebook_url || '',
        twitter_url: aboutData.twitter_url || '',
        instagram_url: aboutData.instagram_url || '',
        youtube_url: aboutData.youtube_url || '',
      })
      // setCurrentCoverImageUrl(aboutData.cover_image || null)
      // setNewCoverImage(null)
    } else {
      setFormState({
        sub_title: '',
        mission: '',
        vision: '',
        facebook_url: '',
        twitter_url: '',
        instagram_url: '',
        youtube_url: '',
      })
      // setCurrentCoverImageUrl(null)
      // setNewCoverImage(null)
    }
    setErrors({})
  }, [aboutData])

  const handleChange = e => {
    setFormState({ ...formState, [e.target.name]: e.target.value })
    setErrors({ ...errors, [e.target.name]: null })
  }

  const handleSubmit = e => {
    e.preventDefault()
    setErrors({})

    const formData = new FormData()
    Object.keys(formState).forEach(key => {
      formData.append(key, formState[key])
    })

    createOrUpdateAbout(formData, {
      onSuccess: () => {
        toast({
          title: 'About Us information saved.',
          description: 'Your About Us details have been updated.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        })
      },
      onError: error => {
        const errorMessages = error?.response?.data?.errors || {}
        setErrors(errorMessages)
        toast({
          title: 'Validation Error',
          description:
            error?.response?.data?.message ||
            'Please check the form for errors.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
        })
      },
    })
  }

  if (isLoading) {
    return (
      <Box p={8} textAlign='center'>
        <Spinner size='xl' color='brand.500' />
        <Heading size='md' mt={4}>
          Loading About Us Information...
        </Heading>
      </Box>
    )
  }

  if (isError) {
    return (
      <VStack spacing={4} align='center' mt='20'>
        <Alert status='error' variant='subtle'>
          <AlertIcon />
          <Box>
            <AlertTitle>Error Occurred!</AlertTitle>
            <AlertDescription>
              Unable to load About Us data. Please try again later.
            </AlertDescription>
          </Box>
        </Alert>
        <Button
          onClick={() => window.location.reload()}
          colorScheme='red'
          isLoading={isLoading}
        >
          Retry
        </Button>
      </VStack>
    )
  }

  return (
    <Card>
      <Box p={6}>
        <Text fontSize='2xl' fontWeight='bold' mb={4}>
          About Us
        </Text>
        <form onSubmit={handleSubmit}>
          <VStack spacing={4} align='stretch'>
            {/* Title and Subtitle */}
            <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
              <FormControl
                id='sub_title'
                isInvalid={!!errors.sub_title}
                flex='1'
              >
                <FormLabel>Sub Title *</FormLabel>
                <Input
                  name='sub_title'
                  value={formState.sub_title}
                  onChange={handleChange}
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                <FormErrorMessage>
                  {errors.sub_title && errors.sub_title[0]}
                </FormErrorMessage>
              </FormControl>
            </Flex>
            <FormControl id='mission' isInvalid={!!errors.mission} flex='1'>
              <FormLabel>Mission *</FormLabel>
              <Textarea
                name='mission'
                value={formState.mission}
                onChange={handleChange}
                color={inputColor}
                backgroundColor={backgroundColor}
              />
              <FormErrorMessage>
                {errors.mission && errors.mission[0]}
              </FormErrorMessage>
            </FormControl>
            <FormControl id='vision' isInvalid={!!errors.vision} flex='1'>
              <FormLabel>Vision *</FormLabel>
              <Textarea
                name='vision'
                value={formState.vision}
                onChange={handleChange}
                color={inputColor}
                backgroundColor={backgroundColor}
              />
              <FormErrorMessage>
                {errors.vision && errors.vision[0]}
              </FormErrorMessage>
            </FormControl>

            {/* Social Media URLs */}
            <Text mt={4} fontSize='lg' fontWeight='bold'>
              Social Media Links (Optional)
            </Text>
            <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
              <FormControl
                id='facebook_url'
                isInvalid={!!errors.facebook_url}
                flex='1'
              >
                <FormLabel>Facebook URL</FormLabel>
                <Input
                  name='facebook_url'
                  value={formState.facebook_url}
                  onChange={handleChange}
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                <FormErrorMessage>
                  {errors.facebook_url && errors.facebook_url[0]}
                </FormErrorMessage>
              </FormControl>

              <FormControl
                id='twitter_url'
                isInvalid={!!errors.twitter_url}
                flex='1'
              >
                <FormLabel>Twitter URL</FormLabel>
                <Input
                  name='twitter_url'
                  value={formState.twitter_url}
                  onChange={handleChange}
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                <FormErrorMessage>
                  {errors.twitter_url && errors.twitter_url[0]}
                </FormErrorMessage>
              </FormControl>
            </Flex>

            <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
              <FormControl
                id='instagram_url'
                isInvalid={!!errors.instagram_url}
                flex='1'
              >
                <FormLabel>Instagram URL</FormLabel>
                <Input
                  name='instagram_url'
                  value={formState.instagram_url}
                  onChange={handleChange}
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                <FormErrorMessage>
                  {errors.instagram_url && errors.instagram_url[0]}
                </FormErrorMessage>
              </FormControl>

              <FormControl
                id='youtube_url'
                isInvalid={!!errors.youtube_url}
                flex='1'
              >
                <FormLabel>YouTube URL</FormLabel>
                <Input
                  name='youtube_url'
                  value={formState.youtube_url}
                  onChange={handleChange}
                  color={inputColor}
                  backgroundColor={backgroundColor}
                />
                <FormErrorMessage>
                  {errors.youtube_url && errors.youtube_url[0]}
                </FormErrorMessage>
              </FormControl>
            </Flex>

            <Button
              mt={4}
              colorScheme='blue'
              type='submit'
              isLoading={isPending}
              // alignSelf={{ base: 'stretch', md: 'flex-start' }}
            >
              Save Changes
            </Button>
          </VStack>
        </form>
      </Box>
    </Card>
  )
}
